import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { useSearchParams } from 'react-router'
import { signUpWithUsernamePasswordV2 } from '../../store/auth/authActions'
import { clearAuth, selectAuthErrors, selectLoginChallenge, selectBlockedLandingPageHtml } from '../../store/auth/authSlice'
import * as React from 'react'
import { useEffect, useState } from 'react'
import MailLogo from '../../assets/images/auth/mail-logo.svg'
import { type PendingProvider, SocialButtons, SocialSignupResidencyStep } from './SocialButtons'
import { PasswordForm } from './PasswordForm'
import { Button } from 'primereact/button'
import { EmailInput } from './EmailInput'
import { AuthSeparator } from './Separator'
import { AuthErrorToast } from './AuthErrorToast'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { CaptchaNote } from './CaptchaNote'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'
import { Col, Container, Row } from 'react-bootstrap'
import { Promo } from './Promo'
import { useCompanySso } from '../../common/companySso'
import { encodeState } from './utils'
import './Auth.scss'
import ExpiredLinkPanel from './errors/ExpiredLinkPanel'
import useExpiredLinkPanel from './useExpiredLinkPanel'
import { SignUpViewHeader } from './headers/SignUpViewHeader'
import BlockedLandingPage from './errors/BlockedLandingPage'
import { ResidencyStep } from './ResidencyStep'
import { DEFAULT_RESIDENCY, type Residency } from '../../models/Residency'
import { useResidencySelection } from './useResidencySelection'
import useInviteContext from './useInviteContext'
import type { InviteContext } from '../../api/auth'
import { SignupVerification } from './SignupVerification'

const SignUpViewV2: React.FC = () => {
  const dispatch = useAppDispatch()
  const blockedLandingPageHtml = useAppSelector(selectBlockedLandingPageHtml)
  const authErrors = useAppSelector(selectAuthErrors)
  const expiredLinkPanel = useExpiredLinkPanel(authErrors)
  const { inviteContext, loading: inviteContextLoading } = useInviteContext()

  useEffect(() => {
    dispatch(clearAuth())
  }, [dispatch])

  if (blockedLandingPageHtml) {
    return <BlockedLandingPage htmlContent={blockedLandingPageHtml} />
  }

  if (expiredLinkPanel) {
    return (
      <div className='auth-container'>
        <ExpiredLinkPanel {...expiredLinkPanel} />
      </div>
    )
  }

  return (
    <div className='auth-container'>
      <AuthErrorToast />
      <SignUpViewHeader inviteContext={inviteContext} inviteContextLoading={inviteContextLoading} />
      {!inviteContextLoading && <SignupStep inviteContext={inviteContext} />}
    </div>
  )
}

export default SignUpViewV2

const SignupStep: React.FC<{ inviteContext?: InviteContext }> = ({ inviteContext }) => {
  const [emailSignup, setEmailSignup] = useState<boolean>(false)
  const [pendingProvider, setPendingProvider] = useState<PendingProvider | null>(null)
  const residencySelectionEnabled = useResidencySelection()

  const toSocialSignup = () => setEmailSignup(false)
  const toEmailSignup = () => setEmailSignup(true)

  if (emailSignup) {
    return <EmailSignup toSocialSignup={toSocialSignup} inviteContext={inviteContext} />
  }
  if (residencySelectionEnabled && pendingProvider) {
    return <SocialSignupResidencyStep provider={pendingProvider} onBack={() => setPendingProvider(null)} />
  }
  return <SocialSignup toEmailSignup={toEmailSignup} onProviderSelected={setPendingProvider} inviteContext={inviteContext} />
}

const SocialSignup: React.FC<{
  toEmailSignup: () => void
  onProviderSelected: (provider: PendingProvider) => void
  inviteContext?: InviteContext
}> = ({ toEmailSignup, onProviderSelected, inviteContext }) => {
  const { shouldUseCompanySso, getCompanySpecificIdPs } = useCompanySso()
  const companySsoEnabled = shouldUseCompanySso()
  const companyIdPs = getCompanySpecificIdPs()
  const companyName = companyIdPs[0]?.displayName
  const isHeaderPlaceholder = companySsoEnabled && !companyName
  const headerText = companySsoEnabled ? (companyName ? `Zocks for ${companyName}` : 'Zocks') : 'Create an Account'
  return (
    <Container className='auth-popup-container'>
      <Row>
        <Col className='p-0 col-xs-12'>
          <div className='auth-popup-inner'>
            {inviteContext ? (
              <InviteLandingHeader inviteContext={inviteContext} />
            ) : (
              <>
                <h2 className={classNames('auth-popup-header', { invisible: isHeaderPlaceholder })}>{headerText}</h2>
                {!!companyName && <div className='auth-footer-text'>Use your {companyName} login. We'll set up your account on first sign in.</div>}
              </>
            )}
            <SignupButtons toEmailSignup={toEmailSignup} onProviderSelected={onProviderSelected} />
            <SignupFooter />
          </div>
        </Col>
        {!companySsoEnabled && !inviteContext && (
          <Col className='p-0 d-none d-lg-block'>
            <Promo />
          </Col>
        )}
      </Row>
    </Container>
  )
}

const InviteLandingHeader: React.FC<{ inviteContext: InviteContext }> = ({ inviteContext }) => (
  <div className='d-flex flex-column align-items-center gap-3 w-100'>
    <h2 className='auth-popup-header'>
      Join <strong>{inviteContext.accountName}</strong> on Zocks
    </h2>
    {!!inviteContext.inviterName && (
      <div className='bg-light border rounded p-2 text-center w-100'>
        <div className='fw-bold'>{inviteContext.inviterName}</div>
        <div className='auth-popup-text'>Invited you to join</div>
      </div>
    )}
  </div>
)

const SignupButtons: React.FC<{ toEmailSignup: () => void; onProviderSelected: (provider: PendingProvider) => void }> = ({
  toEmailSignup,
  onProviderSelected,
}) => {
  const { shouldUseCompanySso } = useCompanySso()
  const companySsoEnabled = shouldUseCompanySso()
  return (
    <div className='auth-buttons'>
      <SocialButtons buttonText={companySsoEnabled ? 'Continue with ' : 'Sign up with '} flow='signup' onProviderSelected={onProviderSelected} />
      {!companySsoEnabled && (
        <>
          <AuthSeparator />
          <button className='auth-email-button' onClick={toEmailSignup}>
            <img src={MailLogo} className='auth-button-logo' alt='Email logo' />
            <span className='auth-email-button-text'>Sign up with Email</span>
          </button>
        </>
      )}
    </div>
  )
}

type EmailSignupFormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword?: string
  state: string | null
  residency: Residency
}

type EmailSignupStep = 'details' | 'residency'

const CAPTCHA_PENDING_MESSAGE = "We're still loading security verification. If this doesn't clear, reload the page and try again."

const EmailSignup: React.FC<{ toSocialSignup: () => void; inviteContext?: InviteContext }> = ({ toSocialSignup, inviteContext }) => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('state')
  const bdSource = searchParams.get('bd_source')
  const state = encodeState({ bd_source: bdSource, code })
  const { executeRecaptcha } = useGoogleReCaptcha()
  const residencySelectionEnabled = useResidencySelection()
  const [step, setStep] = useState<EmailSignupStep>('details')
  const [loading, setLoading] = useState<boolean>(false)

  const form = useForm<EmailSignupFormValues>({
    defaultValues: {
      firstName: inviteContext?.firstName ?? '',
      lastName: inviteContext?.lastName ?? '',
      email: inviteContext?.email ?? '',
      password: '',
      confirmPassword: '',
      state,
      residency: DEFAULT_RESIDENCY,
    },
  })
  const { getValues, setValue } = form

  useEffect(() => {
    setValue('state', state)
  }, [setValue, state])

  const onSubmit = async () => {
    if (!executeRecaptcha || loading) {
      return
    }

    setLoading(true)
    try {
      const captchaToken = await executeRecaptcha('signup')
      const { firstName, lastName, email, password, state, residency } = getValues()
      await dispatch(
        signUpWithUsernamePasswordV2({
          firstName,
          lastName,
          email,
          password,
          captchaToken,
          state,
          residency,
        }),
      )
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const loginChallenge = useAppSelector(selectLoginChallenge)

  if (loginChallenge) {
    return (
      <div className='email-signup-container'>
        <div className='auth-popup-container'>
          <div className='auth-popup-inner'>
            <SignupVerification email={getValues('email')} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='email-signup-container'>
      <button className='auth-link-button' onClick={step === 'residency' ? () => setStep('details') : toSocialSignup} disabled={loading} type='button'>
        <span className='icon-chevron-left auth-back-arrow' />
        <span className='auth-link-text'>Back</span>
      </button>
      <div className='auth-popup-container'>
        <div className='auth-popup-inner'>
          <FormProvider {...form}>
            {step === 'details' ? (
              <>
                {residencySelectionEnabled ? (
                  <SignupEmailForm onContinue={() => setStep('residency')} loading={loading} inviteContext={inviteContext} />
                ) : (
                  <SignupEmailForm
                    onContinue={onSubmit}
                    loading={loading}
                    buttonLabel={inviteContext ? 'Sign up with Email' : 'Create account'}
                    buttonIcon={!!inviteContext}
                    disabled={!executeRecaptcha}
                    disabledMessage={CAPTCHA_PENDING_MESSAGE}
                    inviteContext={inviteContext}
                  />
                )}
                <SignupFooter />
              </>
            ) : (
              <SignupResidencyStep onSubmit={onSubmit} loading={loading} captchaReady={!!executeRecaptcha} />
            )}
          </FormProvider>
        </div>
      </div>
    </div>
  )
}

const SignupResidencyStep: React.FC<{ onSubmit: () => void; loading: boolean; captchaReady: boolean }> = ({ onSubmit, loading, captchaReady }) => {
  const { control } = useFormContext<EmailSignupFormValues>()

  return (
    <Controller
      name='residency'
      control={control}
      render={({ field }) => (
        <ResidencyStep
          value={field.value}
          onChange={field.onChange}
          onSubmit={onSubmit}
          loading={loading}
          disabled={!captchaReady}
          disabledMessage={CAPTCHA_PENDING_MESSAGE}
        />
      )}
    />
  )
}

const SignupEmailForm: React.FC<{
  onContinue: () => void
  loading: boolean
  buttonLabel?: string
  buttonIcon?: boolean
  disabled?: boolean
  disabledMessage?: string
  inviteContext?: InviteContext
}> = ({ onContinue, loading, buttonLabel = 'Continue', buttonIcon = false, disabled, disabledMessage, inviteContext }) => {
  const { control, handleSubmit, register } = useFormContext<EmailSignupFormValues>()

  return (
    <>
      {inviteContext ? (
        <div className='text-center'>
          <h2 className='auth-popup-header'>Sign up for Zocks</h2>
          <div className='auth-popup-text'>
            and join <strong>{inviteContext.accountName}</strong> account
          </div>
        </div>
      ) : (
        <h2 className='auth-popup-header'>Create an account</h2>
      )}
      <form className='email-signup-form-container' method='post' onSubmit={handleSubmit(onContinue)}>
        <input {...register('state')} type='hidden' />
        <div className='auth-input-field'>
          <label htmlFor='firstName' className='auth-form-label'>
            First name
          </label>
          <Controller
            name='firstName'
            control={control}
            rules={{
              validate: (value) => {
                if (!value) {
                  return 'First name is required.'
                }
                if (!value.trim()) {
                  return 'First name must be at least 1 character.'
                }
                return true
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <InputText
                  placeholder='Enter your first name'
                  disabled={loading}
                  className={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                  id={field.name}
                  {...field}
                />
                {!!fieldState.error && <span className='p-error'>{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>
        <div className='auth-input-field'>
          <label htmlFor='lastName' className='auth-form-label'>
            Last name
          </label>
          <Controller
            name='lastName'
            control={control}
            rules={{
              validate: (value) => {
                if (!value) {
                  return 'Last name is required.'
                }
                if (!value.trim()) {
                  return 'Last name must be at least 1 character.'
                }
                return true
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <InputText
                  placeholder='Enter your last name'
                  disabled={loading}
                  className={classNames('auth-input', { 'auth-input-error': fieldState.error })}
                  id={field.name}
                  {...field}
                />
                {!!fieldState.error && <span className='p-error'>{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>
        <EmailInput loading={loading} />
        {!!inviteContext?.email && <InviteEmailNotice invitedEmail={inviteContext.email} />}
        <PasswordForm loading={loading} />
        <CaptchaNote />
        <Button className='auth-email-button' type='submit' loading={loading} disabled={disabled}>
          {buttonIcon && <i className='bi bi-envelope me-2' />}
          <span className='auth-email-button-text'>{buttonLabel}</span>
        </Button>
        {disabled && disabledMessage && <div className='auth-inline-notice'>{disabledMessage}</div>}
      </form>
    </>
  )
}

const InviteEmailNotice: React.FC<{ invitedEmail: string }> = ({ invitedEmail }) => {
  const email = useWatch<EmailSignupFormValues, 'email'>({ name: 'email' })

  if (!email || email.trim().toLowerCase() === invitedEmail.trim().toLowerCase()) {
    return null
  }

  return (
    <div className='bg-light border rounded p-3 d-flex gap-2 w-100'>
      <i className='bi bi-info-circle-fill' />
      <div>
        <div className='fw-bold'>Change the email address?</div>
        <div className='auth-popup-text text-start'>
          The invitation was sent to <u>{invitedEmail}</u>. You can provide a different email, but keep in mind, that{' '}
          <strong>you cannot change your email address after sign up.</strong>
        </div>
      </div>
    </div>
  )
}

const SignupFooter = () => {
  return (
    <div className='auth-footer-text'>
      By creating an account, you agree to our{' '}
      <a href='https://www.zocks.io/terms-of-service' target='_blank' rel='noreferrer'>
        Terms of Service
      </a>{' '}
      and{' '}
      <a href='https://www.zocks.io/privacy-policy' target='_blank' rel='noreferrer'>
        Privacy & Cookie Statement
      </a>
      .
    </div>
  )
}
